import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { publicSupportClient, safeSupportUrl } from "./api";
import type { SupportAnswer, SupportChatClient, SupportRating, SupportStatus } from "./api";
import "./SupportChatWidget.css";

const SUGGESTIONS = ["How do I connect Instagram?", "What can staff access?", "How do follow-ups work?"];
const MIN_QUESTION_LENGTH = 3;
const MAX_QUESTION_LENGTH = 1000;
type Exchange = { id: number; question: string; answer?: SupportAnswer; error?: string };

function Busy() {
  return <span className="support-chat-spinner" aria-hidden="true" />;
}

function AnswerActions({ answer, client }: { answer: SupportAnswer; client: SupportChatClient }) {
  const [feedback, setFeedback] = useState<SupportRating | null>(null);
  const [pending, setPending] = useState<SupportRating | null>(null);
  const [notice, setNotice] = useState("");
  const [copying, setCopying] = useState(false);
  const active = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; active.current?.abort(); };
  }, []);

  async function rate(rating: SupportRating) {
    if (active.current || feedback) return;
    const controller = new AbortController();
    active.current = controller;
    setPending(rating);
    setNotice("");
    try {
      await client.feedback(answer.request_id, rating, controller.signal);
      if (!controller.signal.aborted) { setFeedback(rating); setNotice("Thank you for your feedback."); }
    } catch {
      if (!controller.signal.aborted) setNotice("Feedback could not be saved. Please try again.");
    } finally {
      if (!controller.signal.aborted) { active.current = null; setPending(null); }
    }
  }

  async function copy() {
    setCopying(true);
    setNotice("");
    try {
      await navigator.clipboard.writeText(answer.answer);
      if (mounted.current) setNotice("Answer copied.");
    } catch {
      if (mounted.current) setNotice("Copy is unavailable. You can select and copy the answer text.");
    } finally {
      if (mounted.current) setCopying(false);
    }
  }

  return <div className="support-chat-answer-actions">
    <div className="support-chat-action-row" role="group" aria-label="Answer feedback">
      <button type="button" onClick={copy} disabled={copying}>{copying && <Busy />} {copying ? "Copying…" : "Copy answer"}</button>
      <button type="button" aria-pressed={feedback === "helpful"} disabled={!!pending || !!feedback} onClick={() => rate("helpful")}>
        {pending === "helpful" && <Busy />} Helpful
      </button>
      <button type="button" aria-pressed={feedback === "not_helpful"} disabled={!!pending || !!feedback} onClick={() => rate("not_helpful")}>
        {pending === "not_helpful" && <Busy />} Not helpful
      </button>
    </div>
    <span role="status">{notice}</span>
  </div>;
}

export function SupportChatWidget({ client = publicSupportClient }: { client?: SupportChatClient }) {
  const [status, setStatus] = useState<SupportStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [busy, setBusy] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const active = useRef<AbortController | null>(null);
  const nextId = useRef(0);
  const launcher = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const panel = useRef<HTMLElement>(null);
  const history = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const questionId = useId();
  const panelId = useId();

  useEffect(() => {
    const controller = new AbortController();
    client.getStatus(controller.signal).then((value) => {
      if (!controller.signal.aborted) setStatus(value);
    }).catch(() => { /* Hide an unavailable help service; do not disrupt the page. */ });
    return () => { controller.abort(); active.current?.abort(); };
  }, [client]);

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = priorOverflow; };
  }, [open]);

  useEffect(() => {
    if (history.current) history.current.scrollTop = history.current.scrollHeight;
  }, [exchanges, busy]);

  function reset() {
    active.current?.abort();
    active.current = null;
    setBusy(false);
    setQuestion("");
    setExchanges([]);
    setAnnouncement("Conversation cleared.");
    input.current?.focus();
  }

  function close() {
    reset();
    setOpen(false);
    launcher.current?.focus();
  }

  async function ask(text: string, retryId?: number) {
    const trimmed = text.trim();
    if (Array.from(trimmed).length < MIN_QUESTION_LENGTH || trimmed.length > MAX_QUESTION_LENGTH || active.current) return;
    const id = retryId ?? ++nextId.current;
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    if (retryId === undefined) setQuestion("");
    setAnnouncement("Finding product guidance.");
    setExchanges((current) => retryId === undefined
      ? [...current.slice(-19), { id, question: trimmed }]
      : current.map((item) => item.id === id ? { id, question: trimmed } : item));
    // Move focus off a disappearing suggestion/retry button immediately. Do not
    // take it back later if the user reads a source while the answer is loading.
    input.current?.focus();
    try {
      const answer = await client.ask(trimmed, controller.signal);
      if (controller.signal.aborted) return;
      setExchanges((current) => current.map((item) => item.id === id ? { ...item, answer } : item));
      setAnnouncement("Answer ready. Review the product guidance and sources below.");
    } catch (error) {
      if (controller.signal.aborted) return;
      const message = error instanceof Error ? error.message : "We could not answer right now. Please try again.";
      setExchanges((current) => current.map((item) => item.id === id ? { ...item, error: message } : item));
      setAnnouncement("Your question could not be answered. You can retry.");
    } finally {
      if (active.current === controller) {
        active.current = null;
        setBusy(false);
      }
    }
  }

  function submit(event: FormEvent) { event.preventDefault(); void ask(question); }
  function keyboard(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") { event.preventDefault(); close(); return; }
    if (event.key !== "Tab") return;
    const candidates = panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], textarea:not(:disabled), summary, [tabindex="0"]');
    const focusable = Array.from(candidates ?? []);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  if (!status?.available || status.mode === "disabled") return null;
  const supportUrl = safeSupportUrl(status.support_url);
  const questionLength = Array.from(question.trim()).length;

  return <div className="support-chat">
    <button ref={launcher} type="button" className="support-chat-launcher" aria-expanded={open} aria-controls={panelId} aria-haspopup="dialog" onClick={() => { setOpen(true); setAnnouncement(""); }}>
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 3v-3a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.7" /><path d="M7 9h10M7 13h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
      Ask LeadCat
    </button>
    {open && <div className="support-chat-overlay">
      <section ref={panel} id={panelId} className="support-chat-panel" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} onKeyDown={keyboard}>
        <header className="support-chat-header">
          <div><span className="support-chat-eyebrow">{status.mode === "reviewer_preview" ? "PRIVATE REVIEWER PREVIEW" : "PRODUCT HELP"}</span><h2 id={titleId}>Ask LeadCat</h2></div>
          <div className="support-chat-header-actions">
            <button type="button" onClick={reset}>Clear chat</button>
            <button type="button" onClick={close} aria-label="Close and clear chat">✕</button>
          </div>
        </header>
        <p id={descriptionId} className="support-chat-privacy">General product guidance in English. Each question stands alone. Please do not share passwords, OTPs, or customer details. This chat clears when closed or when you change pages.</p>
        {status.mode === "preview" && <p className="support-chat-preview">Internal preview · draft knowledge, not approved for customer use.</p>}
        {status.mode === "reviewer_preview" && <p className="support-chat-preview">Private reviewer preview · draft knowledge, not approved for customer use. Access is limited to the configured reviewer account and workspace. This is general product guidance, not account-specific support.</p>}
        <p className="support-chat-sr-only" role="status" aria-live="polite">{announcement}</p>
        <div ref={history} className="support-chat-history" role="log" aria-label="Product help conversation" aria-live="polite" aria-relevant="additions text" aria-busy={busy} tabIndex={0}>
          {exchanges.length === 0 && <div className="support-chat-welcome">
            <h3>How can we help you get started?</h3>
            <p>Ask about onboarding, integrations, roles, or everyday CRM features.</p>
            <div className="support-chat-suggestions">{SUGGESTIONS.map((suggestion) => <button key={suggestion} type="button" onClick={() => ask(suggestion)} disabled={busy}>{suggestion}<span aria-hidden="true">↗</span></button>)}</div>
          </div>}
          {exchanges.map((item) => <article key={item.id} className="support-chat-exchange" aria-label={`Question ${item.id}`}>
            <div className="support-chat-question"><span>You</span><p>{item.question}</p></div>
            <div className="support-chat-answer">
              <span className="support-chat-speaker">LeadCat product help</span>
              {item.answer && <>
                {item.answer.requires_confirmation && <p className="support-chat-confirmation">Requires product confirmation</p>}
                <p className="support-chat-answer-text">{item.answer.answer}</p>
                {item.answer.citations.length > 0 && <details className="support-chat-sources">
                  <summary>Sources ({item.answer.citations.length})</summary>
                  {item.answer.citations.map((citation) => <div key={citation.section_id} className="support-chat-source">
                    <strong>{citation.title}</strong><span>{citation.heading}</span>
                    <blockquote>{citation.excerpt}</blockquote>
                    <small>Verified: {citation.verified_at}</small>
                  </div>)}
                  {item.answer.knowledge_version && <small className="support-chat-version">Knowledge version: {item.answer.knowledge_version}</small>}
                </details>}
                {item.answer.escalation_required && <p className="support-chat-escalation">{supportUrl ? <a href={supportUrl} target="_blank" rel="noopener noreferrer">Contact support for help ↗</a> : "This needs a support team member. Use your usual LeadCat support channel."}</p>}
                <AnswerActions answer={item.answer} client={client} />
              </>}
              {item.error && <div className="support-chat-error" role="alert"><p>{item.error}</p><button type="button" onClick={() => ask(item.question, item.id)} disabled={busy}>Retry question</button></div>}
              {!item.answer && !item.error && <p className="support-chat-loading"><Busy /> Finding product guidance…</p>}
            </div>
          </article>)}
        </div>
        <form className="support-chat-form" onSubmit={submit}>
          <label htmlFor={questionId}>Your product question</label>
          <textarea ref={input} id={questionId} value={question} onChange={(event) => setQuestion(event.target.value)} maxLength={MAX_QUESTION_LENGTH} rows={2} placeholder="e.g. How do I connect my Facebook Page?" aria-describedby={`${questionId}-hint`} onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void ask(question); }
          }} />
          <div className="support-chat-form-bottom"><small id={`${questionId}-hint`}>{questionLength > 0 && questionLength < MIN_QUESTION_LENGTH ? "Use at least 3 characters" : "Shift + Enter for a new line"} · {question.length}/{MAX_QUESTION_LENGTH}</small><button className="support-chat-send" type="submit" disabled={busy || questionLength < MIN_QUESTION_LENGTH}>{busy && <Busy />}{busy ? "Answering…" : "Send question"}</button></div>
        </form>
        <footer className="support-chat-footer">Answers use our product knowledge. {supportUrl && <a href={supportUrl} target="_blank" rel="noopener noreferrer">Contact support ↗</a>}</footer>
      </section>
    </div>}
  </div>;
}
